/**
 * DOM-basierter Login/Register-Screen.
 * Wird zeitlich VOR dem LoadingScreen angezeigt — nach Login/Dev-Mode
 * wird er entfernt und der LoadingScreen gestartet.
 */
import { createInput, createButton, createTabBtn, createMsgBox, createDiv, inputStyle } from './LoginFormElements';

type AuthCallback = (accountName: string, password: string) => void;
type RegisterCallback = (accountName: string, email: string, password: string) => void;
type DevCallback = (code: string) => void;

export class LoginScreen {
  private _root!: HTMLDivElement;
  private _card!: HTMLDivElement;
  private _errorEl!: HTMLDivElement;
  private _confirmEl!: HTMLDivElement;
  private _loginForm!: HTMLDivElement;
  private _registerForm!: HTMLDivElement;
  private _devInput!: HTMLInputElement;
  private _tabLogin!: HTMLButtonElement;
  private _tabRegister!: HTMLButtonElement;
  private _submitBtns: HTMLButtonElement[] = [];
  private _onLogin: AuthCallback | null = null;
  private _onRegister: RegisterCallback | null = null;
  private _onDev: DevCallback | null = null;
  private _keyHandler: (e: KeyboardEvent) => void;

  constructor() {
    this._keyHandler = (e: KeyboardEvent) => {
      if (e.ctrlKey && e.shiftKey && e.key === 'C') {
        e.preventDefault();
        this._devInput.style.display = this._devInput.style.display === 'none' ? 'block' : 'none';
        if (this._devInput.style.display === 'block') this._devInput.focus();
      }
    };
    this._build();
  }

  show(): void {
    document.body.appendChild(this._root);
    document.addEventListener('keydown', this._keyHandler);
    // Oberen Abstand fixieren, damit die Card nur nach unten wächst
    const topGap = (this._root.clientHeight - this._card.offsetHeight) / 2;
    this._root.style.alignItems = 'flex-start';
    this._root.style.paddingTop = `${Math.max(topGap, 32)}px`;
  }
  hide(): void {
    this._root.style.opacity = '0';
    document.removeEventListener('keydown', this._keyHandler);
    setTimeout(() => this._root.remove(), 400);
  }

  onLogin(cb: AuthCallback): void { this._onLogin = cb; }
  onRegister(cb: RegisterCallback): void { this._onRegister = cb; }
  onDevMode(cb: DevCallback): void { this._onDev = cb; }

  showError(msg: string): void {
    this._errorEl.textContent = msg;
    this._errorEl.style.display = msg ? 'block' : 'none';
    this._confirmEl.style.display = 'none';
  }

  showConfirmation(msg: string): void {
    this._confirmEl.textContent = msg;
    this._confirmEl.style.display = 'block';
    this._errorEl.style.display = 'none';
  }

  /** Wechselt zum Login-Tab, behält Bestätigungshinweis bei. */
  switchToLogin(): void {
    this._switchTab(this._tabLogin, this._tabRegister, true, true);
  }

  setLoading(loading: boolean): void {
    for (const btn of this._submitBtns) {
      btn.disabled = loading;
      btn.style.opacity = loading ? '0.5' : '1';
    }
  }

  private _build(): void {
    this._root = createDiv({
      position: 'fixed', inset: '0', zIndex: '10001', background: '#000',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      fontFamily: "'Segoe UI', system-ui, sans-serif", transition: 'opacity 0.4s',
    });
    this._card = createDiv({
      maxWidth: '360px', width: '90%', padding: 'clamp(20px,4vw,32px)',
      background: 'rgba(10,12,20,0.95)', border: '1px solid rgba(136,204,255,0.15)',
      borderRadius: '12px',
    });
    this._root.appendChild(this._card);
    const card = this._card;

    const title = document.createElement('div');
    Object.assign(title.style, {
      fontSize: 'clamp(16px, 4vw, 20px)', fontWeight: '300', letterSpacing: '4px',
      textTransform: 'uppercase', color: '#8cf', textAlign: 'center', marginBottom: '28px',
    });
    title.textContent = 'Imperial Star Rising';
    card.appendChild(title);

    const tabs = createDiv({ display: 'flex', marginBottom: '20px', gap: '0' });
    this._tabLogin = createTabBtn('Anmelden', true);
    this._tabRegister = createTabBtn('Registrieren', false);
    tabs.append(this._tabLogin, this._tabRegister);
    card.appendChild(tabs);

    this._errorEl = createMsgBox('#f44');
    this._confirmEl = createMsgBox('#4f4');
    card.append(this._errorEl, this._confirmEl);

    this._buildLoginForm(card);
    this._buildRegisterForm(card);
    this._tabLogin.addEventListener('click', () => this._switchTab(this._tabLogin, this._tabRegister, true));
    this._tabRegister.addEventListener('click', () => this._switchTab(this._tabRegister, this._tabLogin, false));

    this._devInput = document.createElement('input');
    Object.assign(this._devInput.style, { ...inputStyle(), display: 'none', marginTop: '16px', borderColor: '#553' });
    this._devInput.placeholder = 'dev code...';
    this._devInput.addEventListener('keydown', (e) => {
      if (e.key === 'Enter' && this._onDev) this._onDev(this._devInput.value.trim());
    });
    card.appendChild(this._devInput);
  }

  private _buildLoginForm(card: HTMLDivElement): void {
    this._loginForm = createDiv({});
    const name = createInput('Account-Name', 'text');
    const pw = createInput('Passwort', 'password');
    const btn = createButton('Anmelden');
    btn.addEventListener('click', () => { if (this._onLogin) this._onLogin(name.value.trim(), pw.value); });
    pw.addEventListener('keydown', (e) => { if (e.key === 'Enter') btn.click(); });
    this._loginForm.append(name, pw, btn);
    this._submitBtns.push(btn);
    card.appendChild(this._loginForm);
  }

  private _buildRegisterForm(card: HTMLDivElement): void {
    this._registerForm = createDiv({ display: 'none' });
    const name = createInput('Account-Name', 'text');
    const email = createInput('E-Mail', 'email');
    const pw = createInput('Passwort', 'password');
    const pw2 = createInput('Passwort bestätigen', 'password');
    const btn = createButton('Registrieren');
    btn.addEventListener('click', () => {
      if (pw.value !== pw2.value) { this.showError('Passwörter stimmen nicht überein'); return; }
      if (this._onRegister) this._onRegister(name.value.trim(), email.value.trim(), pw.value);
    });
    pw2.addEventListener('keydown', (e) => { if (e.key === 'Enter') btn.click(); });
    this._registerForm.append(name, email, pw, pw2, btn);
    this._submitBtns.push(btn);
    card.appendChild(this._registerForm);
  }

  private _switchTab(
    active: HTMLButtonElement, inactive: HTMLButtonElement,
    isLogin: boolean, keepMessages = false,
  ): void {
    active.style.color = '#8cf';
    active.style.borderBottomColor = '#8cf';
    inactive.style.color = '#556';
    inactive.style.borderBottomColor = 'transparent';
    this._loginForm.style.display = isLogin ? 'block' : 'none';
    this._registerForm.style.display = isLogin ? 'none' : 'block';
    if (!keepMessages) {
      this._errorEl.style.display = 'none';
      this._confirmEl.style.display = 'none';
    }
  }
}
